<template>
<Teleport to="body">
  <div class="fixed inset-0 z-50 flex justify-center bg-black/60">
    <div class="m-5 w-full md:w-8/12 lg:w-4/12 xl:w-3/12" @click.self="closeDialog(false)" >

      <div class="p-0 bg-gray-50 shadow-xl border border-gray-200 rounded-md text-center">
        
        <div class="flex flex-col w-full text-black">
        
          <BlockUI :visible="blocked">

            <div v-if="blocked" class="absolute inset-0 flex items-center justify-center z-50 bg-black/20">
              <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="5" fill="transparent" animationDuration=".5s" />
            </div>

            <div class="flex flex-col w-full rounded-t-md p-3 bg-gradient-to-r from-[#424c42] to-[#647064]">
              <div>
                <font-awesome-icon :icon="['fas', 'fa-lock']" class="w-6 h-6 text-white" />
              </div>
              <div class="font-semibold text-white text-sm md:text-2xl truncate whitespace-nowrap overflow-hidden text-ellipsis"> 
                {{ $t('titulo.cambiar.password') }}
              </div>
            </div>

            <Form :validation-schema="schema" @invalid-submit="onInvalidSubmit">

              <div class="container p-4 text-left space-y-3">

                <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
                  <div class="w-full px-2 md:w-1/1">
                    <label class="block mb-1">{{ $t('label.contrasenia.actual') }}</label>
                    <Field name="claveActual" v-slot="{ field, errors }" v-model="claveActual">
                      <InputText v-bind="field" type="password" class="input-base" :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0}" />
                      <span class="text-sm text-red-500 p-error"><ErrorMessage name="claveActual" /></span>
                    </Field>
                  </div>
                </div>
                <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
                  <div class="w-full px-2 md:w-1/1">
                    <label class="block mb-1">{{ $t('label.contrasenia.nueva') }}</label>
                    <Field name="claveNueva" v-slot="{ field, errors }" v-model="claveNueva">
                      <InputText v-bind="field" type="password" class="input-base" :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0}" />
                      <span class="text-sm text-red-500 p-error"><ErrorMessage name="claveNueva" /></span>
                    </Field>
                  </div>
                </div>
                <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
                  <div class="w-full px-2 md:w-1/1">
                    <label class="block mb-1">{{ $t('label.repetir.contrasenia') }}</label>
                    <Field name="claveRepetida" v-slot="{ field, errors }" v-model="claveRepetida">
                      <InputText v-bind="field" type="password" class="input-base" :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0}" />
                      <span class="text-sm text-red-500 p-error"><ErrorMessage name="claveRepetida" /></span>
                    </Field>
                  </div>
                </div>

              </div>

            </Form>

            <div class="p-3 flex flex-col md:flex-row gap-2 justify-end w-full mt-2 bg-gray-100">
              <a @click="closeDialog(false)" class="cursor-pointer px-6 text-sm font-bold text-red-500 bg-transparent py-3 border border-red-500 shadow-md rounded-xl transform transition-transform duration-200 hover:scale-105 active:scale-95">
                <font-awesome-icon :icon="['fas', 'fa-xmark']" /> {{ $t('boton.cancelar') }}
              </a>
              <a @click="aplicarCambios" class="cursor-pointer px-6 text-sm font-bold text-[#424c42] bg-transparent py-3 border border-[#424c42] shadow-md rounded-xl transform transition-transform duration-200 hover:scale-105 active:scale-95">
                <font-awesome-icon :icon="['fas', 'fa-check-double']" /> {{ $t('boton.aceptar') }}
              </a>
            </div>
          </BlockUI>


        </div>
      </div>

    </div>
  </div>
</Teleport>  

</template>

<script lang="ts">
import * as yup from 'yup';

import { defineComponent } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';

import Swal from 'sweetalert2'
import InputText from 'primevue/inputtext';
import BlockUI from 'primevue/blockui';
import ProgressSpinner from 'primevue/progressspinner';

import { ConfiguracionMeeting } from 'qreventos-suite/src/services/suite.interfaces';
import seguridadServices from '@/services/seguridad.services';
import configuracionMeetingService from '@/services/configuracion.merting.services';
import suiteService, { Acreditado } from 'qreventos-suite/services';

export default defineComponent({
  components: {
    InputText, BlockUI, ProgressSpinner,
    Field, ErrorMessage, Form
  },
  props: {
    closeFunction: {
      type: Function,
      required: false
    }
  },
  data() {
    return {
      blocked: false,
      acreditado: undefined as unknown as Acreditado,
      claveActual: '',
      claveNueva: '',
      claveRepetida: '',
    };
  },
  computed: {
    schema() {
      return yup.object({
        claveActual: yup.string().required(this.$t('errors.ingresa.contrasenia')).min(8, this.$t('errors.min.8.caracteres')),
        claveNueva: yup.string().required(this.$t('errors.ingresa.nueva.contrasenia')).min(8, this.$t('errors.min.8.caracteres')),
        claveRepetida: yup.string().required(this.$t('errors.repetir.nueva.contrasenia')).min(8, this.$t('errors.min.caracteres')).oneOf([yup.ref('claveNueva')], this.$t('errors.contrasenias.no.coinciden')),
      });
    }
  },
  methods: {
    onInvalidSubmit(values: any) {
      const errores: number = Object.values(values?.errors || {}).flat().length;
      Swal.fire({ title: "Error", html: this.$t("error.revise"), icon: "error"});
    },
    closeDialog(reload: boolean = false) {
      if (this.closeFunction) {
        this.closeFunction(reload);
      }
    },
    clearCambiarClave() {
      this.claveActual = '';
      this.claveNueva = '';
      this.claveRepetida = '';
    },   
    async aplicarCambios() {
      try {
        this.blocked = true;

        const hash: string = seguridadServices.getLocalHash() as string;
        const configuracionMeeting:ConfiguracionMeeting = configuracionMeetingService.get() as ConfiguracionMeeting;

        await suiteService.changePassword(configuracionMeeting.evento.id, hash, this.claveActual, this.claveNueva);
        this.closeDialog(true);
      }
      catch (error) {
        this.blocked = false;
        Swal.fire({ title: "Error", text: 'Error [' + error + ']', icon: "error" });
      } finally {
        this.blocked = false;
      }
    }, 
  },
  async mounted() {
    document.body.style.overflow = "hidden";
    this.clearCambiarClave();
  },
  unmounted() {
    document.body.style.overflow = "";
  }
});
</script>